import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, Search, Download, FileSpreadsheet, FileText, File as FilePdf, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Building2 } from 'lucide-react';
import { format } from 'date-fns';
import { exportToExcel, exportToCSV, exportToPDF } from '../../utils/export';

interface Participant {
  student_id: string;
  first_name: string;
  last_name: string;
  first_name_tamil?: string;
  last_name_tamil?: string;
  date_of_birth: string;
  gender: string;
  house_id: string;
  house?: {
    house_id: string;
    house_name: string;
    house_color: string;
  };
  reference_number?: string;
  payment_status?: string;
  payment_date?: string;
  payment_notes?: string;
}

interface Department {
  department_id: string;
  name: string;
  name_tamil?: string;
  created_at: string;
  hidden_in_exports?: boolean;
  registration_code?: string;
}

type SortDirection = 'asc' | 'desc';

interface SortConfig {
  key: string;
  direction: SortDirection;
}

const ITEMS_PER_PAGE = 15;

const DepartmentDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  
  const [department, setDepartment] = useState<Department | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filtering and sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGender, setSelectedGender] = useState<string>('All');
  const [selectedYear, setSelectedYear] = useState<string>('All');
  const [selectedHouse, setSelectedHouse] = useState<string>('All');
  const [birthYears, setBirthYears] = useState<number[]>([]);
  const [houses, setHouses] = useState<{house_id: string, house_name: string}[]>([]);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  
  // Export menu
  const [showExportMenu, setShowExportMenu] = useState(false);

  useEffect(() => {
    if (id) {
      fetchDepartmentData();
    }
  }, [id]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedGender, selectedYear, selectedHouse, sortConfig]);

  const fetchDepartmentData = async () => {
    try {
      setLoading(true);
      
      // Fetch department details
      const { data: departmentData, error: departmentError } = await supabase
        .from('departments')
        .select('*')
        .eq('department_id', id)
        .single();
      
      if (departmentError) throw departmentError;
      setDepartment(departmentData);
      
      // Fetch participants belonging to this department
      const { data: participantsData, error: participantsError } = await supabase
        .from('students')
        .select(`
          *,
          house:houses(
            house_id,
            house_name,
            house_color
          )
        `)
        .eq('department_id', id)
        .order('last_name');
      
      if (participantsError) throw participantsError;
      setParticipants(participantsData || []);
      
      // Extract unique birth years for filtering
      const years = [...new Set(participantsData?.map(p => 
        new Date(p.date_of_birth).getFullYear()
      ) || [])].sort((a, b) => b - a);
      setBirthYears(years);
      
      // Fetch houses for filtering
      const { data: housesData, error: housesError } = await supabase
        .from('houses')
        .select('house_id, house_name')
        .order('house_name');
      
      if (housesError) throw housesError;
      setHouses(housesData || []);
      
    } catch (error: any) {
      console.error('Error fetching department data:', error);
      setError(error.message || 'Failed to load department data');
    } finally {
      setLoading(false);
    }
  };

  const requestSort = (key: string) => {
    let direction: SortDirection = 'asc';
    
    if (sortConfig && sortConfig.key === key) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }
    
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey: string) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return null;
    }
    
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 inline-block ml-1" /> 
      : <ArrowDown className="h-4 w-4 inline-block ml-1" />;
  };

  // Apply filters and sorting
  const getFilteredAndSortedParticipants = () => {
    // First apply filters
    const filtered = participants.filter(participant => {
      const matchesSearch = 
        `${participant.first_name} ${participant.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (participant.first_name_tamil?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (participant.last_name_tamil?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (participant.reference_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (participant.house?.house_name || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesGender = selectedGender === 'All' || 
        (selectedGender === 'Male' && participant.gender === 'M') ||
        (selectedGender === 'Female' && participant.gender === 'F');
      
      const participantYear = new Date(participant.date_of_birth).getFullYear();
      const matchesYear = selectedYear === 'All' || participantYear === parseInt(selectedYear);

      const matchesHouse = selectedHouse === 'All' || participant.house_id === selectedHouse;
      
      return matchesSearch && matchesGender && matchesYear && matchesHouse;
    });

    // Then apply sorting if configured
    if (sortConfig !== null) {
      return [...filtered].sort((a, b) => {
        // Handle nested properties with dot notation
        const getNestedProperty = (obj: any, path: string) => {
          return path.split('.').reduce((prev, curr) => {
            return prev ? prev[curr] : null;
          }, obj);
        };
        
        let aValue = getNestedProperty(a, sortConfig.key);
        let bValue = getNestedProperty(b, sortConfig.key);
        
        // Special handling for date_of_birth
        if (sortConfig.key === 'date_of_birth') {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }
        
        // Handle different data types
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        // For numbers, dates, etc.
        return sortConfig.direction === 'asc' 
          ? (aValue > bValue ? 1 : -1)
          : (bValue > aValue ? 1 : -1);
      });
    }

    return filtered;
  };

  const filteredParticipants = getFilteredAndSortedParticipants();
  const totalPages = Math.ceil(filteredParticipants.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedParticipants = filteredParticipants.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleExport = (format: 'excel' | 'csv' | 'pdf') => {
    switch (format) {
      case 'excel':
        exportToExcel(filteredParticipants);
        break;
      case 'csv':
        exportToCSV(filteredParticipants);
        break;
      case 'pdf':
        exportToPDF(filteredParticipants);
        break;
    }
    setShowExportMenu(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!department) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">Department not found</div>
        <button
          onClick={() => navigate('/departments')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          {t('departments.actions.cancel')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Building2 className="h-6 w-6 mr-2 text-blue-600" />
              {department.name}
            </h1>
            {department.name_tamil && (
              <p className="text-lg text-gray-600" style={{ fontFamily: 'Noto Sans Tamil, Arial, sans-serif' }}>
                {department.name_tamil}
              </p>
            )}
            <p className="text-sm text-gray-500 mt-1">
              {filteredParticipants.length} {t('departments.table.students')}
            </p>
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowExportMenu(!showExportMenu)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Download className="h-4 w-4 mr-2" />
            {t('departments.actions.export')}
          </button>
          
          {showExportMenu && (
            <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
              <div className="py-1" role="menu">
                <button
                  onClick={() => handleExport('excel')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  {t('participants.export.toExcel')}
                </button>
                <button
                  onClick={() => handleExport('csv')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  {t('participants.export.toCSV')}
                </button>
                <button
                  onClick={() => handleExport('pdf')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <FilePdf className="h-4 w-4 mr-2" />
                  {t('participants.export.toPDF')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Department Info Card */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 bg-blue-50">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Department Information</h3>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200">
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Name (English)</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{department.name}</dd>
            </div>
            {department.name_tamil && (
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Name (Tamil)</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" style={{ fontFamily: 'Noto Sans Tamil, Arial, sans-serif' }}>
                  {department.name_tamil}
                </dd>
              </div>
            )}
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Visibility in Exports</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  department.hidden_in_exports ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                }`}>
                  {department.hidden_in_exports ? t('departments.visibility.hidden') : t('departments.visibility.visible')}
                </span>
              </dd>
            </div>
            {department.registration_code && (
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Registration Code</dt>
                <dd className="mt-1 text-sm font-mono bg-gray-100 px-2 py-1 rounded text-gray-900 sm:mt-0 sm:col-span-2">
                  {department.registration_code}
                </dd>
              </div>
            )}
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Total Participants</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {participants.length}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 md:items-center">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('participants.filters.searchPlaceholder')}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedGender}
                onChange={(e) => setSelectedGender(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('participants.filters.allGenders')}</option>
                <option value="Male">{t('participants.filters.male')}</option>
                <option value="Female">{t('participants.filters.female')}</option>
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('participants.filters.allBirthYears')}</option>
                {birthYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedHouse}
                onChange={(e) => setSelectedHouse(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('houses.title')}</option>
                {houses.map(house => (
                  <option key={house.house_id} value={house.house_id}>{house.house_name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {filteredParticipants.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {t('participants.noStudents.title')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedGender !== 'All' || selectedYear !== 'All' || selectedHouse !== 'All'
                ? t('participants.noStudents.description.withFilters')
                : t('participants.noStudents.description.noFilters')}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('first_name')}
                    >
                      {t('participants.table.name')}
                      {getSortIcon('first_name')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('first_name_tamil')}
                    >
                      {t('participants.table.tamilName')}
                      {getSortIcon('first_name_tamil')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('date_of_birth')}
                    >
                      {t('participants.table.dateOfBirth')}
                      {getSortIcon('date_of_birth')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('gender')}
                    >
                      {t('participants.table.gender')}
                      {getSortIcon('gender')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('house.house_name')}
                    >
                      {t('participants.table.house')}
                      {getSortIcon('house.house_name')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('reference_number')}
                    >
                      {t('participants.table.referenceNumber')}
                      {getSortIcon('reference_number')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('payment_status')}
                    >
                      {t('participants.form.fields.paymentStatus.label')}
                      {getSortIcon('payment_status')}
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">{t('participants.table.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedParticipants.map((participant) => (
                    <tr key={participant.student_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {participant.first_name} {participant.last_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {participant.first_name_tamil} {participant.last_name_tamil}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">
                          {format(new Date(participant.date_of_birth), 'dd.MM.yyyy')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          participant.gender === 'M' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'
                        }`}>
                          {participant.gender === 'M' ? t('participants.filters.male') : t('participants.filters.female')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div 
                            className="h-3 w-3 rounded-full mr-2" 
                            style={{ backgroundColor: participant.house?.house_color }}
                          />
                          <div className="text-sm text-gray-900">
                            {participant.house?.house_name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {participant.reference_number || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          participant.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                          participant.payment_status === 'school_paid' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {participant.payment_status === 'paid' ? t('reports.payments.status.paid') :
                           participant.payment_status === 'school_paid' ? t('reports.payments.status.school_paid') :
                           t('reports.payments.status.not_paid')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link to={`/participants/${participant.student_id}`} className="text-blue-600 hover:text-blue-800">
                          {t('participants.actions.view')}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                      <span className="font-medium">
                        {Math.min(startIndex + ITEMS_PER_PAGE, filteredParticipants.length)}
                      </span>{' '}
                      of <span className="font-medium">{filteredParticipants.length}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                      >
                        <span className="sr-only">Previous</span>
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                      >
                        <span className="sr-only">Next</span>
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DepartmentDetails;